import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, MessageSquare, Users, UserCheck, Mail, LogOut, ChevronDown, ChevronUp, Plus, Save, X, Lock, BarChart3, Download } from "lucide-react";
import { WhatsAppIcon } from "@/components/Footer";
import { API, img, inrFmt } from "@/lib/format";
import useSEO from "@/hooks/useSEO";

const STATUSES = ["Placed", "Confirmed", "Shipped", "Out for Delivery", "Delivered", "Cancelled"];
const TOKEN_KEY = "jk_admin_token";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const sizesToText = (sizes) => (sizes || []).map((s) => `${s.size}:${s.stock}`).join(", ");
const textToSizes = (text, originalSizes = []) =>
  (text || "").split(",").map((pair) => {
    const [size, stock] = pair.trim().split(":");
    if (!size) return null;
    const prev = (originalSizes || []).find((v) => v.size === size.trim().toUpperCase());
    return {
      size: size.trim().toUpperCase(),
      stock: parseInt(stock, 10) || 0,
      ...(prev && prev.price_inr != null ? { price_inr: prev.price_inr, price_usd: prev.price_usd, mrp_inr: prev.mrp_inr } : {}),
    };
  }).filter(Boolean);

const csv = (arr) => (arr || []).join(", ");
const fromCsv = (text) => (text || "").split(",").map((s) => s.trim()).filter(Boolean);

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("snapshot");
  useSEO({ title: "Admin — Just Kords" });

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back!");
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-alabaster px-4 pt-28">
        <form onSubmit={login} className="w-full max-w-sm border border-linec bg-white p-8" data-testid="admin-login-form">
          <span className="inline-flex rounded-full bg-crimson-sub p-3 text-crimson"><Lock size={20} /></span>
          <h1 className="mt-4 font-serif text-3xl">Admin Login</h1>
          <p className="mt-1 text-xs text-mutedc">Just Kords store management</p>
          <label className="mt-6 block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedc">Email</span>
            <input data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full border border-linec px-4 py-3 text-sm focus:border-crimson focus:outline-none" />
          </label>
          <label className="mt-4 block">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedc">Password</span>
            <input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full border border-linec px-4 py-3 text-sm focus:border-crimson focus:outline-none" />
          </label>
          {loginError && <p data-testid="admin-login-error" className="mt-4 bg-crimson-sub px-4 py-3 text-xs text-crimson">{loginError}</p>}
          <button data-testid="admin-login-submit" type="submit" className="mt-6 w-full rounded-full bg-crimson py-3.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark">
            Log In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-alabaster pt-28 lg:pt-36">
      <div className="mx-auto max-w-6xl px-4 pb-24 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-linec pb-6">
          <h1 className="font-serif text-4xl font-light">Store Admin</h1>
          <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full border border-ink px-5 py-2 text-sm font-semibold transition-colors hover:bg-ink hover:text-white">
            <LogOut size={15} /> Log out
          </button>
        </div>

        <div className="flex gap-2 py-6">
          {[["snapshot", "Snapshot", BarChart3], ["orders", "Orders", Package], ["customers", "Customers", UserCheck], ["products", "Products", Package], ["messages", "Messages", MessageSquare], ["subscribers", "Subscribers", Mail]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "bg-crimson text-white" : "border border-linec bg-white text-mutedc hover:text-ink"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {tab === "snapshot" && <SnapshotTab />}
        {tab === "orders" && <OrdersTab />}
        {tab === "customers" && <CustomersTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "messages" && <MessagesTab />}
        {tab === "subscribers" && <SubscribersTab />}
      </div>
    </div>
  );
}

function SnapshotTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders() })
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  if (!stats) return <p className="py-16 text-sm text-mutedc">Crunching your numbers…</p>;

  const maxDaily = Math.max(...stats.daily_14d.map((d) => d.revenue), 1);
  const maxFabric = Math.max(...stats.revenue_by_fabric.map((f) => f.revenue), 1);
  const repeatPct = stats.customers_total ? Math.round((stats.repeat_customers / stats.customers_total) * 100) : 0;

  const cards = [
    ["Collected Revenue", inrFmt(stats.revenue_paid), `≈ $${Number(stats.revenue_paid_usd).toFixed(2)} · ${stats.orders_paid} paid order${stats.orders_paid === 1 ? "" : "s"}`],
    ["This Month", inrFmt(stats.revenue_this_month),
      stats.mom_delta_pct === null ? "No sales last month to compare"
        : `${stats.mom_delta_pct >= 0 ? "▲" : "▼"} ${Math.abs(stats.mom_delta_pct)}% vs last month (${inrFmt(stats.revenue_last_month)})`],
    ["Last 7 Days", inrFmt(stats.revenue_last7d), "Collected in the past week"],
    ["Avg Order Value", inrFmt(stats.avg_order_value), `${repeatPct}% of customers reorder`],
  ];

  return (
    <div data-testid="admin-snapshot">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([label, value, sub]) => (
          <div key={label} className="border border-linec bg-white p-5" data-testid={`snapshot-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedc">{label}</p>
            <p className="mt-2 font-serif text-3xl text-crimson">{value}</p>
            <p className="mt-1 text-[11px] text-mutedc">{sub}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 border border-linec bg-white p-6" data-testid="snapshot-daily-chart">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl">Revenue — last 14 days</h3>
          <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">{stats.customers_total} customers · {stats.repeat_customers} repeat</p>
        </div>
        <div className="mt-6 flex h-32 items-end gap-2">
          {stats.daily_14d.map((d) => (
            <div key={d.date} className="group relative flex-1">
              <div
                className="w-full rounded-t bg-crimson transition-all duration-300 hover:bg-crimson-dark"
                style={{ height: `${Math.max((d.revenue / maxDaily) * 110, 4)}px` }}
                title={`${d.date}: ${inrFmt(d.revenue)} · ${d.orders} order(s)`}
              />
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-widest text-mutedc">
          <span>{stats.daily_14d[0].date}</span>
          <span>{stats.daily_14d[6].date}</span>
          <span>Today</span>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="border border-linec bg-white p-6">
          <h3 className="font-serif text-2xl">Top-Selling Sets</h3>
          {stats.top_products.length === 0 ? (
            <p className="mt-4 text-sm text-mutedc">No orders yet — your bestsellers will rank here automatically.</p>
          ) : (
            <div className="mt-4 space-y-3" data-testid="snapshot-top-products">
              {stats.top_products.map((p, i) => (
                <div key={p.name} className="flex items-center gap-4 border-b border-linec pb-3 last:border-0 last:pb-0">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full font-mono text-xs ${i === 0 ? "bg-crimson text-white" : "bg-crimson-sub text-crimson"}`}>{i + 1}</span>
                  <p className="min-w-0 flex-1 truncate text-sm font-semibold">{p.name}</p>
                  <p className="font-mono text-xs text-mutedc">{p.qty} sold</p>
                  <p className="text-sm font-semibold">{inrFmt(p.revenue)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-linec bg-white p-6">
          <h3 className="font-serif text-2xl">Revenue by Fabric</h3>
          {stats.revenue_by_fabric.length === 0 ? (
            <p className="mt-4 text-sm text-mutedc">Fabric performance appears here as orders come in — useful for planning next month's buys.</p>
          ) : (
            <div className="mt-4 space-y-3" data-testid="snapshot-fabric-bars">
              {stats.revenue_by_fabric.map((f) => (
                <div key={f.fabric}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold">{f.fabric}</span>
                    <span className="font-mono text-mutedc">{inrFmt(f.revenue)}</span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-linec">
                    <div className="h-full rounded-full bg-crimson" style={{ width: `${(f.revenue / maxFabric) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-mutedc">Ordered value, cancelled orders excluded — plan next month's stock on this</p>
        </div>
      </div>

      <div className="mt-6 border border-linec bg-white p-6" data-testid="snapshot-order-book">
        <h3 className="font-serif text-2xl">Order Book</h3>
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-5">
          {[
            ["Paid & confirmed", stats.orders_paid],
            ["Awaiting payment", stats.orders_awaiting_payment],
            ["Cash on delivery", stats.orders_cod],
            ["Cancelled", stats.orders_cancelled],
            ["Total orders", stats.orders_total],
          ].map(([label, count]) => (
            <div key={label} className="border border-linec p-4 text-center">
              <p data-testid={`snapshot-orders-${label.toLowerCase().replace(/\s+/g, "-")}`} className="font-mono text-2xl font-semibold text-crimson">{count}</p>
              <p className="mt-1 text-[11px] text-mutedc">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [edits, setEdits] = useState({});

  const load = () => axios.get(`${API}/admin/orders`, { headers: authHeaders() })
    .then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${id}`, edits[id] || {}, { headers: authHeaders() });
      setOrders((o) => o.map((x) => (x.order_id === id ? data : x)));
      const lastEmail = (data.emails_sent || []).slice(-1)[0];
      if (lastEmail) {
        toast.success(`${id} updated — notification email sent to ${lastEmail.to}`);
      } else {
        toast.success(`${id} updated — customer sees "${data.status}" on tracking`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setEdit = (id, key, value) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [key]: value } }));

  const waNotifyLink = (o) => {
    const digits = (o.phone || "").replace(/\D/g, "");
    const to = digits.length === 10 ? `91${digits}` : digits;
    const shipped = ["Shipped", "Out for Delivery", "Delivered"].includes(o.status);
    const msg = shipped
      ? `Hi ${o.customer_name}! Your Just Kords order ${o.order_id} is ${o.status.toLowerCase()}${o.courier ? ` via ${o.courier}` : ""}${o.tracking_number ? ` (AWB: ${o.tracking_number})` : ""}. Track it anytime: https://justkords.com/track-order?id=${o.order_id} — thank you for shopping with us!`
      : `Hi ${o.customer_name}! Your Just Kords order ${o.order_id} is confirmed and being prepared with love. We'll send tracking details the moment it ships. Track anytime: https://justkords.com/track-order?id=${o.order_id}`;
    return `https://wa.me/${to}?text=${encodeURIComponent(msg)}`;
  };

  if (orders.length === 0) return <p className="py-16 text-sm text-mutedc">No orders yet — they'll appear here the moment a customer checks out.</p>;

  return (
    <div className="space-y-4" data-testid="admin-orders-list">
      {orders.map((o) => (
        <div key={o.order_id} className="border border-linec bg-white" data-testid={`admin-order-row-${o.order_id}`}>
          <div className="flex flex-wrap items-center gap-3 p-4">
            <button data-testid={`admin-order-expand-${o.order_id}`} onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)}
              className="font-mono text-sm font-medium text-crimson">
              {o.order_id} {expanded === o.order_id ? <ChevronUp size={14} className="inline" /> : <ChevronDown size={14} className="inline" />}
            </button>
            <span className="text-sm font-semibold">{o.customer_name}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-mutedc">{o.items.length} item{o.items.length > 1 ? "s" : ""} · {inrFmt(o.total_inr)}</span>
            <span className={`ml-auto px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${o.payment_status === "paid" ? "bg-green-100 text-green-800" : o.payment_status === "cod_pending" ? "bg-amber-100 text-amber-800" : "bg-linec text-mutedc"}`}>
              {o.payment_method === "cod" ? "COD" : o.payment_status}
            </span>
          </div>
          <div className="flex flex-wrap items-end gap-3 border-t border-linec bg-[#FCFBF9] p-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Status</span>
              <select data-testid={`admin-order-status-${o.order_id}`} value={(edits[o.order_id] || {}).status ?? o.status}
                onChange={(e) => setEdit(o.order_id, "status", e.target.value)}
                className="mt-1 block border border-linec bg-white px-3 py-2 text-sm focus:border-crimson focus:outline-none">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Courier</span>
              <input data-testid={`admin-order-courier-${o.order_id}`} value={(edits[o.order_id] || {}).courier ?? o.courier ?? ""}
                onChange={(e) => setEdit(o.order_id, "courier", e.target.value)} placeholder="e.g. Delhivery"
                className="mt-1 block w-36 border border-linec bg-white px-3 py-2 text-sm focus:border-crimson focus:outline-none" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Tracking AWB</span>
              <input data-testid={`admin-order-tracking-${o.order_id}`} value={(edits[o.order_id] || {}).tracking_number ?? o.tracking_number ?? ""}
                onChange={(e) => setEdit(o.order_id, "tracking_number", e.target.value)} placeholder="AWB number"
                className="mt-1 block w-40 border border-linec bg-white px-3 py-2 text-sm focus:border-crimson focus:outline-none" />
            </label>
            <button data-testid={`admin-order-save-${o.order_id}`} onClick={() => save(o.order_id)}
              className="flex items-center gap-1.5 rounded-full bg-crimson px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-crimson-dark">
              <Save size={13} /> Save
            </button>
            <a
              data-testid={`admin-order-whatsapp-${o.order_id}`}
              href={waNotifyLink(o)}
              target="_blank" rel="noopener noreferrer"
              onClick={() => toast.info("WhatsApp opened — press send to notify the customer")}
              className="flex items-center gap-1.5 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            >
              <WhatsAppIcon size={13} /> Notify on WhatsApp
            </a>
          </div>
          {expanded === o.order_id && (
            <div className="grid gap-6 border-t border-linec p-4 sm:grid-cols-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Deliver to</p>
                <p className="mt-2 text-sm leading-relaxed">{o.customer_name} · {o.phone}<br />{o.email}<br />{o.address1}, {o.city}, {o.state} — {o.pincode}, {o.country}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Items</p>
                <div className="mt-2 space-y-2">
                  {o.items.map((i) => (
                    <div key={`${i.slug}:${i.size}`} className="flex items-center gap-3">
                      <img src={img(i.image, 80)} alt="" className="h-10 w-9 object-cover" />
                      <p className="flex-1 text-xs">{i.name} · {i.size} × {i.qty}</p>
                      <p className="text-xs font-semibold">{inrFmt(i.price_inr * i.qty)}</p>
                    </div>
                  ))}
                </div>
                {(o.emails_sent || []).length > 0 && (
                  <div className="mt-4 border-t border-linec pt-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">Emails sent</p>
                    {(o.emails_sent || []).map((e, i) => (
                      <p key={i} data-testid={`admin-order-email-${o.order_id}-${i}`} className="mt-1 text-[11px] text-mutedc">
                        ✓ {e.type} → {e.to} · {new Date(e.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => axios.get(`${API}/admin/products`, { headers: authHeaders() })
    .then((r) => setProducts(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-products-list">
      <button data-testid="admin-add-product" onClick={() => { setAdding(!adding); setEditing(null); }}
        className="mb-4 flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark">
        <Plus size={15} /> Add Product
      </button>
      {adding && <ProductForm onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      <div className="space-y-3">
        {products.map((p) => {
          const stock = (p.sizes || []).reduce((s, v) => s + (v.stock || 0), 0);
          return (
            <div key={p.slug} className="border border-linec bg-white" data-testid={`admin-product-row-${p.slug}`}>
              <div className="flex flex-wrap items-center gap-3 p-3">
                <img src={p.images?.[0] ? img(p.images[0], 80) : ""} alt="" className="h-12 w-10 bg-linec object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.name} {!p.active && <span className="ml-1 bg-linec px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-mutedc">Hidden</span>}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-mutedc">{p.slug} · {p.fabric} · {inrFmt(p.price_inr)} · stock {stock}</p>
                </div>
                <button data-testid={`admin-product-edit-${p.slug}`} onClick={() => { setEditing(editing === p.slug ? null : p.slug); setAdding(false); }}
                  className="rounded-full border border-ink px-4 py-2 text-xs font-semibold transition-colors hover:bg-ink hover:text-white">
                  {editing === p.slug ? "Close" : "Edit"}
                </button>
              </div>
              {editing === p.slug && (
                <ProductForm product={p} onCancel={() => setEditing(null)}
                  onSaved={() => { setEditing(null); load(); }}
                  onDeactivate={async () => {
                    try {
                      await axios.delete(`${API}/admin/products/${p.slug}`, { headers: authHeaders() });
                      toast.success(`${p.name} hidden from the store`);
                      setEditing(null); load();
                    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                  }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProductForm({ product, onCancel, onSaved, onDeactivate }) {
  const [form, setForm] = useState({
    name: product?.name || "", category: product?.category || "Kurta Sets", fabric: product?.fabric || "",
    occasion: product?.occasion || "", description: product?.description || "",
    price_inr: product?.price_inr ?? "", mrp_inr: product?.mrp_inr ?? "",
    colors: csv(product?.colors), images: csv(product?.images), tags: csv(product?.tags),
    sizes: sizesToText(product?.sizes), featured: product?.featured || false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    if (!form.name.trim() || !form.price_inr) { toast.error("Name and price are required"); return; }
    setSaving(true);
    const payload = {
      name: form.name, category: form.category, fabric: form.fabric, occasion: form.occasion,
      description: form.description, price_inr: parseFloat(form.price_inr),
      mrp_inr: form.mrp_inr ? parseFloat(form.mrp_inr) : null,
      colors: fromCsv(form.colors), images: fromCsv(form.images), tags: fromCsv(form.tags),
      sizes: textToSizes(form.sizes, product?.sizes),
      featured: form.featured, active: product ? product.active : true,
    };
    try {
      if (product) {
        await axios.put(`${API}/admin/products/${product.slug}`, payload, { headers: authHeaders() });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
        toast.success("Product added to the store");
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const field = "mt-1 w-full border border-linec bg-white px-3 py-2 text-sm focus:border-crimson focus:outline-none";
  const labelCls = "font-mono text-[10px] uppercase tracking-widest text-mutedc";

  return (
    <div className="border-t border-linec bg-[#FCFBF9] p-4" data-testid={product ? `admin-product-form-${product.slug}` : "admin-product-form-new"}>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2"><span className={labelCls}>Name</span>
          <input data-testid="admin-product-name" value={form.name} onChange={set("name")} className={field} /></label>
        <label className="block"><span className={labelCls}>Category</span>
          <input data-testid="admin-product-category" value={form.category} onChange={set("category")} className={field} /></label>
        <label className="block"><span className={labelCls}>Fabric</span>
          <input data-testid="admin-product-fabric" value={form.fabric} onChange={set("fabric")} className={field} /></label>
        <label className="block"><span className={labelCls}>Price (₹)</span>
          <input data-testid="admin-product-price" type="number" value={form.price_inr} onChange={set("price_inr")} className={field} /></label>
        <label className="block"><span className={labelCls}>MRP (₹, optional)</span>
          <input data-testid="admin-product-mrp" type="number" value={form.mrp_inr} onChange={set("mrp_inr")} className={field} /></label>
        <label className="block"><span className={labelCls}>Occasion (optional)</span>
          <input data-testid="admin-product-occasion" value={form.occasion} onChange={set("occasion")} className={field} /></label>
        <label className="block"><span className={labelCls}>Tags (comma separated)</span>
          <input data-testid="admin-product-tags" value={form.tags} onChange={set("tags")} placeholder="New, Bestseller" className={field} /></label>
        <label className="block sm:col-span-2"><span className={labelCls}>Description</span>
          <textarea data-testid="admin-product-description" rows={3} value={form.description} onChange={set("description")} className={field} /></label>
        <label className="block"><span className={labelCls}>Colours (comma separated)</span>
          <input data-testid="admin-product-colors" value={form.colors} onChange={set("colors")} className={field} /></label>
        <label className="block"><span className={labelCls}>Sizes &amp; stock (M:5,L:3,3XL:2)</span>
          <input data-testid="admin-product-sizes" value={form.sizes} onChange={set("sizes")} className={field} /></label>
        <label className="block sm:col-span-2"><span className={labelCls}>Image URLs (comma separated)</span>
          <input data-testid="admin-product-images" value={form.images} onChange={set("images")} className={field} /></label>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm">
        <input data-testid="admin-product-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-crimson" />
        Feature on homepage
      </label>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button data-testid="admin-product-save" onClick={save} disabled={saving}
          className="flex items-center gap-2 rounded-full bg-crimson px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-crimson-dark disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : product ? "Save Changes" : "Add Product"}
        </button>
        <button onClick={onCancel} className="flex items-center gap-2 rounded-full border border-ink px-6 py-2.5 text-sm font-semibold transition-colors hover:bg-ink hover:text-white">
          <X size={14} /> Cancel
        </button>
        {product && onDeactivate && (
          <button data-testid="admin-product-deactivate" onClick={onDeactivate} className="text-xs font-semibold uppercase tracking-widest text-crimson underline underline-offset-4">
            {product.active ? "Hide from store" : "Restore to store"}
          </button>
        )}
      </div>
    </div>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/customers`, { headers: authHeaders() })
      .then((r) => setCustomers(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  if (!customers) return <p className="py-16 text-sm text-mutedc">Loading your customers…</p>;

  const exportCsv = () => {
    const rows = [["Name", "Email", "Phone", "Wallet Balance", "Orders", "Paid Orders", "Lifetime Value (INR)", "Last Order"],
      ...customers.map((c) => [c.name, c.email, c.phone, c.wallet_balance, c.orders, c.paid_orders, c.lifetime_paid, c.last_order])];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "justkords-customers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const repeat = customers.filter((c) => c.paid_orders >= 2).length;

  return (
    <div data-testid="admin-customers-list">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-mutedc">
          {customers.length} customers · {repeat} repeat · sorted by lifetime value
        </p>
        <button data-testid="admin-customers-export" onClick={exportCsv} className="ml-auto flex items-center gap-2 rounded-full border border-ink px-5 py-2 text-xs font-semibold transition-colors hover:bg-ink hover:text-white">
          <Download size={13} /> Export CSV
        </button>
      </div>
      {customers.length === 0 ? (
        <p className="py-10 text-sm text-mutedc">No customers yet — they appear here with their first order.</p>
      ) : (
        <div className="space-y-3">
          {customers.map((c) => (
            <div key={c.email} className="flex flex-wrap items-center gap-3 border border-linec bg-white p-4" data-testid={`admin-customer-${c.email.replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-crimson-sub font-mono text-xs font-bold text-crimson">{c.name ? c.name[0].toUpperCase() : "?"}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{c.name || "—"}</p>
                <p className="truncate text-xs text-mutedc">{c.email} · {c.orders} order{c.orders !== 1 ? "s" : ""} ({c.paid_orders} paid) · last: {c.last_order || "—"}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold">{inrFmt(c.lifetime_paid)}</p>
                <p className="font-mono text-[10px] text-mutedc" data-testid={`admin-customer-wallet-${c.email.replace(/[^a-z0-9]+/g, "-")}`}>{c.wallet_balance > 0 ? `${inrFmt(c.wallet_balance)} in wallet` : "no cashback"}</p>
              </div>
              <a href={`mailto:${c.email}`} data-testid={`admin-customer-mail-${c.email.replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full border border-linec px-4 py-2 text-xs font-semibold transition-colors hover:border-crimson hover:text-crimson">Email</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/messages`, { headers: authHeaders() })
      .then((r) => setMessages(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  const deleteMsg = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: authHeaders() });
      setMessages((m) => m.filter((x) => x._id !== id));
      toast.success("Message deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const waReplyLink = (m) => {
    const digits = (m.phone || "").replace(/\D/g, "");
    const to = digits.length === 10 ? `91${digits}` : digits;
    const text = `Hi ${m.name}! Thanks for reaching out to Just Kords about your ${m.topic} query — `;
    return `https://wa.me/${to}?text=${encodeURIComponent(text)}`;
  };

  if (messages.length === 0) return <p className="py-16 text-sm text-mutedc">No customer messages yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-messages-list">
      {messages.map((m) => (
        <div key={m._id} className="border border-linec bg-white p-4" data-testid={`admin-message-${m._id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold">{m.name}</p>
            <span className="bg-crimson-sub px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-crimson">{m.topic}</span>
            <span className="ml-auto font-mono text-[10px] text-mutedc">{new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-4">
            <a href={`mailto:${m.email}`} className="text-xs text-crimson underline underline-offset-4">{m.email}</a>
            {m.phone && (
              <a data-testid={`admin-message-whatsapp-${m._id}`} href={waReplyLink(m)} target="_blank" rel="noopener noreferrer"
                onClick={() => toast.info("WhatsApp opened — finish the message and hit send")}
                className="flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-1.5 text-[11px] font-semibold text-white transition-opacity hover:opacity-90">
                <WhatsAppIcon size={12} /> Reply on WhatsApp
              </a>
            )}
            <button data-testid={`admin-message-delete-${m._id}`} onClick={() => deleteMsg(m._id)}
              className="ml-auto font-mono text-[10px] uppercase tracking-widest text-mutedc transition-colors duration-200 hover:text-crimson">
              Delete
            </button>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-mutedc">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function SubscribersTab() {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/subscribers`, { headers: authHeaders() })
      .then((r) => setSubs(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  return (
    <div data-testid="admin-subscribers-list">
      <p className="pb-4 font-mono text-[11px] uppercase tracking-widest text-mutedc">{subs.length} members of the Kords Club</p>
      <div className="flex flex-wrap gap-2">
        {subs.map((s) => (
          <a key={s.email} href={`mailto:${s.email}`} className="rounded-full border border-linec bg-white px-4 py-2 text-xs">{s.email}</a>
        ))}
      </div>
    </div>
  );
}
